import { useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { FileText, Upload, CheckCircle, AlertCircle, Loader2, Briefcase, ChevronRight, Mail, ShieldCheck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, Button, Badge, Input, useToast, Logo03Png, Logo01Png } from '@hiring-intelligence/ui';
import { YEARS_OF_EXPERIENCE_LABELS } from '@hiring-intelligence/shared';
import { candidateApi } from '@/services/api';

function MarkdownContent({ content }: { content: string }) {
	return (
		<div className="prose prose-sm max-w-none dark:prose-invert overflow-x-auto prose-table:w-full prose-table:border-collapse prose-th:border prose-th:border-border prose-th:px-3 prose-th:py-2 prose-th:bg-muted prose-th:text-left prose-td:border prose-td:border-border prose-td:px-3 prose-td:py-2">
			<ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
		</div>
	);
}

function JobDescriptionCard({ job, showUpload, token }: { job: any; showUpload: boolean; token: string }) {
	const { toast } = useToast();
	const [file, setFile] = useState<File | null>(null);
	const [dragActive, setDragActive] = useState(false);
	const [uploaded, setUploaded] = useState(false);

	const uploadMutation = useMutation({
		mutationFn: (formData: FormData) => candidateApi.uploadCV(formData),
		onSuccess: () => {
			setUploaded(true);
			toast({ title: 'Success', description: 'CV uploaded successfully!' });
		},
		onError: (error: Error) => {
			toast({ title: 'Error', description: error.message, variant: 'destructive' });
		}
	});

	const handleDrag = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		if (e.type === 'dragenter' || e.type === 'dragover') {
			setDragActive(true);
		} else if (e.type === 'dragleave') {
			setDragActive(false);
		}
	}, []);

	const handleDrop = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setDragActive(false);
		if ((e as any).dataTransfer.files && (e as any).dataTransfer.files[0]) {
			setFile((e as any).dataTransfer.files[0]);
		}
	}, []);

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if ((e.target as any).files && (e.target as any).files[0]) {
			setFile((e.target as any).files[0]);
		}
	};

	const handleUpload = () => {
		if (!file) return;
		const uploadToken = job.token || token;
		const formData = new FormData();
		formData.append('token', uploadToken);
		formData.append('cv', file);
		uploadMutation.mutate(formData);
	};

	const hasUploaded = job.hasUploaded || uploaded;

	return (
		<div className="space-y-4">
			{/* Job Header */}
			<div className="text-center">
				<h2 className="text-xl sm:text-2xl font-bold text-gray-900">{job.roleTitle}</h2>
				<div className="flex flex-wrap items-center justify-center gap-2 mt-2 text-gray-500">
					{job.yearsOfExperience && <Badge variant="secondary">{YEARS_OF_EXPERIENCE_LABELS[job.yearsOfExperience] || job.yearsOfExperience}</Badge>}
					{job.location && <Badge variant="outline">{job.location}</Badge>}
					{job.company && <span className="text-sm">{job.company.name}</span>}
				</div>
			</div>

			{/* Job Description */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center gap-2">
						<FileText className="h-5 w-5" />
						Job Description
						{job.jdType === 'blind' && (
							<Badge variant="secondary" className="ml-2">Confidential</Badge>
						)}
					</CardTitle>
				</CardHeader>
				<CardContent>
					{job.jobDescription ? (
						typeof job.jobDescription === 'string' ? (
							<MarkdownContent content={job.jobDescription} />
						) : (
							<pre className="text-sm bg-gray-50 p-4 rounded-lg overflow-auto">
								{JSON.stringify(job.jobDescription, null, 2)}
							</pre>
						)
					) : (
						<p className="text-muted-foreground">Job description is being prepared.</p>
					)}
				</CardContent>
			</Card>

			{/* CV Upload */}
			{showUpload && (
				<Card>
					<CardHeader>
						<CardTitle className="flex items-center gap-2">
							<Upload className="h-5 w-5" />
							Upload Your CV
						</CardTitle>
					</CardHeader>
					<CardContent>
						{hasUploaded ? (
							<div className="flex flex-col items-center py-8">
								<CheckCircle className="h-16 w-16 text-green-500 mb-4" />
								<h3 className="text-lg font-semibold mb-2">CV Submitted!</h3>
								<p className="text-muted-foreground text-center">
									Your CV has been uploaded successfully. The recruitment team will review your application.
								</p>
							</div>
						) : (
							<>
							<div
									className={`border-2 border-dashed rounded-xl p-4 sm:p-8 text-center transition-colors ${dragActive
										? 'border-primary bg-primary/5'
										: 'border-gray-200 hover:border-gray-300'
										}`}
									onDragEnter={handleDrag}
									onDragLeave={handleDrag}
									onDragOver={handleDrag}
									onDrop={handleDrop}
								>
									{file ? (
										<div className="flex flex-col items-center">
											<FileText className="h-10 w-10 text-primary mb-3" />
											<p className="font-medium">{file.name}</p>
											<p className="text-sm text-muted-foreground mt-1">
												{(file.size / 1024 / 1024).toFixed(2)} MB
											</p>
											<Button variant="ghost" size="sm" className="mt-2" onClick={() => setFile(null)}>
												Remove
											</Button>
										</div>
									) : (
										<div className="flex flex-col items-center">
											<Upload className="h-10 w-10 text-gray-300 mb-3" />
											<p className="font-medium mb-1">Drop your CV here</p>
											<p className="text-sm text-muted-foreground mb-3">PDF, DOC, DOCX (max 10MB)</p>
											<label className="cursor-pointer">
												<Button variant="outline" size="sm" asChild>
													<span>Browse Files</span>
												</Button>
												<input type="file" className="hidden" accept=".pdf" onChange={handleFileChange} />
											</label>
										</div>
									)}
								</div>
								{file && (
									<Button className="w-full mt-4" onClick={handleUpload} disabled={uploadMutation.isPending}>
										{uploadMutation.isPending ? (
											<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Uploading...</>
										) : (
											<><Upload className="mr-2 h-4 w-4" /> Upload CV</>
										)}
									</Button>
								)}
							</>
						)}
					</CardContent>
				</Card>
			)}
		</div>
	);
}

export default function CandidatePortalPage() {
	const { token } = useParams<{ token: string }>();
	const { toast } = useToast();
	const queryClient = useQueryClient();
	const [selectedJobIndex, setSelectedJobIndex] = useState<number>(0);
	const [verifyEmail, setVerifyEmail] = useState('');

	const { data, isLoading, error } = useQuery({
		queryKey: ['candidate-portal', token],
		queryFn: () => candidateApi.getJobInfo(token!),
		enabled: !!token
	});

	const verifyMutation = useMutation({
		mutationFn: () => candidateApi.verifyEmail(token!, verifyEmail.trim()),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['candidate-portal', token] });
		},
		onError: (err: Error) => {
			toast({ title: 'Verification Failed', description: err.message, variant: 'destructive' });
		}
	});

	if (isLoading) {
		return (
			<div className="min-h-screen bg-gray-50 flex items-center justify-center">
				<Loader2 className="h-8 w-8 animate-spin text-gray-400" />
			</div>
		);
	}

	if (error) {
		return (
			<div className="min-h-screen bg-gray-50 flex items-center justify-center">
				<Card className="max-w-md w-full mx-4">
					<CardContent className="flex flex-col items-center py-12">
						<AlertCircle className="h-12 w-12 text-red-500 mb-4" />
						<h2 className="text-xl font-semibold mb-2">Link Invalid or Expired</h2>
						<p className="text-muted-foreground text-center">
							This link may have expired or is no longer valid. Please contact the recruiter for a new link.
						</p>
					</CardContent>
				</Card>
			</div>
		);
	}

	const jobInfo = data?.data;

	// Email verification required
	if (jobInfo?.requiresEmailVerification) {
		return (
			<div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center px-4">
				<div className="w-full max-w-md">
					<div className="text-center mb-8">
						<div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-primary/10 mb-4">
							<img src={Logo03Png} alt="Canis Major" className="h-7 w-7" />
						</div>
						<div className="flex justify-center mb-4">
							<img src={Logo01Png} alt="Canis Major" className="h-8" />
						</div>
						{jobInfo.candidate?.name && (
							<p className="text-lg text-gray-600 mb-1">
								Hi, <span className="font-semibold text-gray-800">{jobInfo.candidate.name}</span>
							</p>
						)}
						<h1 className="text-xl font-bold text-gray-900 mt-2">{jobInfo.roleTitle}</h1>
					</div>

					<Card>
						<CardHeader className="text-center">
							<div className="mx-auto h-14 w-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
								<Mail className="h-7 w-7 text-primary" />
							</div>
							<CardTitle>Verify Your Email</CardTitle>
							<CardDescription>
								Please enter your email address to access the job description.
							</CardDescription>
						</CardHeader>
						<CardContent>
							<form onSubmit={(e) => { e.preventDefault(); verifyMutation.mutate(); }} className="space-y-4">
								<div className="space-y-2">
									<label className="text-sm font-medium text-gray-700">Email Address</label>
									<Input
										type="email"
										placeholder="your@email.com"
										value={verifyEmail}
										onChange={(e) => setVerifyEmail(e.target.value)}
										autoFocus
									/>
									<p className="text-xs text-muted-foreground">
										Use the same email your recruiter contacted you with.
									</p>
								</div>
								<Button
									type="submit"
									className="w-full"
									disabled={!verifyEmail.trim() || verifyMutation.isPending}
								>
									{verifyMutation.isPending ? (
										<><Loader2 className="h-4 w-4 animate-spin mr-2" /> Verifying...</>
									) : (
										<><ShieldCheck className="mr-2 h-4 w-4" /> Verify Email</>
									)}
								</Button>
							</form>
						</CardContent>
					</Card>
				</div>
			</div>
		);
	}

	const otherJobs: any[] = jobInfo?.otherJobs || [];

	// Build array of all jobs: current job first, then others
	const currentJob = {
		token: token,
		roleTitle: jobInfo?.roleTitle,
		yearsOfExperience: jobInfo?.yearsOfExperience,
		location: jobInfo?.location,
		jdType: jobInfo?.jdType,
		hasUploaded: jobInfo?.hasUploaded,
		company: jobInfo?.company,
		jobDescription: jobInfo?.jobDescription
	};
	const allJobs = [currentJob, ...otherJobs];
	const hasMultipleJobs = allJobs.length > 1;
	const selectedJob = allJobs[selectedJobIndex] || currentJob;

	return (
		<div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
			<div className="max-w-3xl mx-auto px-4 py-8">
				{/* Header */}
				<div className="text-center mb-8">
					<div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-primary/10 mb-4">
						<img src={Logo03Png} alt="Canis Major" className="h-7 w-7" />
					</div>
					<div className="flex justify-center mb-2">
						<img src={Logo01Png} alt="Canis Major" className="h-8" />
					</div>
					{jobInfo?.candidate?.name && (
						<p className="text-lg text-gray-600 mb-1">
							Hi, <span className="font-semibold text-gray-800">{jobInfo.candidate.name}</span>
						</p>
					)}
					{hasMultipleJobs && (
						<p className="text-gray-500 mt-1">
							You have {allJobs.length} open position{allJobs.length > 1 ? 's' : ''} to review.
						</p>
					)}
				</div>

				{/* Job switcher tabs when multiple JDs */}
				{hasMultipleJobs && (
					<div className="mb-6 space-y-2">
						{allJobs.map((job, index) => (
							<Card
								key={job.token || index}
								className={`cursor-pointer transition-all ${selectedJobIndex === index
									? 'ring-2 ring-primary shadow-md'
									: 'hover:shadow-md opacity-80'
									}`}
								onClick={() => setSelectedJobIndex(index)}
							>
								<CardContent className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3">
							<div className="flex items-center gap-3 min-w-0">
									<div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${selectedJobIndex === index ? 'bg-primary/15' : 'bg-gray-100'
											}`}>
											<Briefcase className={`h-4 w-4 ${selectedJobIndex === index ? 'text-primary' : 'text-gray-400'}`} />
										</div>
									<div className="min-w-0">
										<p className="font-medium text-sm truncate">{job.roleTitle}</p>
										<div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
												{job.yearsOfExperience && <span>{YEARS_OF_EXPERIENCE_LABELS[job.yearsOfExperience] || job.yearsOfExperience}</span>}
												{job.location && <span>• {job.location}</span>}
												{job.company?.name && <span>• {job.company.name}</span>}
											</div>
										</div>
									</div>
									<div className="flex items-center gap-2 sm:ml-auto shrink-0">
										{job.jdType === 'blind' && <Badge variant="secondary" className="text-xs">Confidential</Badge>}
										<ChevronRight className={`h-4 w-4 transition-transform ${selectedJobIndex === index ? 'text-primary rotate-90' : 'text-muted-foreground'}`} />
									</div>
								</CardContent>
							</Card>
						))}
					</div>
				)}

				{/* Selected Job Detail */}
				<JobDescriptionCard
					job={selectedJob}
					showUpload={selectedJobIndex === 0}
					token={token!}
				/>

				{/* Footer */}
				<p className="text-center text-xs text-gray-400 mt-8">
					This link expires in 48 hours. Powered by Canis Major.
				</p>
			</div>
		</div>
	);
}
